import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { shoppingList } from '@/features/search/api/search'
import type { ProductCustomCardItem } from '@/types/ProductType'
import type {
  shoppingListPayload,
  shoppingListParams,
  SortingOptions,
  PageInfo
} from '@/types/searchItemsTypes'
import type { arrayOfFilters } from '@/types/types'

export const useSimilarItems = defineStore('similarItems', () => {
  // State
  const sourceImageUrl = ref<string | null>(null)
  const similarItems = ref<ProductCustomCardItem[]>([])
  const currentPage = ref<number>(1)
  const pageInfo = ref<PageInfo | null>(null)
  const totalCount = ref<number>(0)
  const currentFilters = ref<arrayOfFilters>([])
  const currentSort = ref<SortingOptions>('default')
  const isLoading = ref<boolean>(false)
  const error = ref<string | null>(null)

  // Actions
  const setSourceImage = (url: string) => {
    sourceImageUrl.value = url
  }

  const getSimilarItems = async (
    payload: shoppingListPayload,
    params: shoppingListParams
  ) => {
    isLoading.value = true
    error.value = null

    try {
      const response = await shoppingList(payload, params)

      if (response.data) {
        similarItems.value = response.data.search_results ?? []
        pageInfo.value = response.data.page_info ?? null
        totalCount.value = response.data.total_quantity ?? 0
        // FACET-1: null on pages 2+ means "not recomputed" — keep the
        // page-1 payload; only [] means empty (FE_CONTRACT_CHANGES.md)
        currentFilters.value = response.data.dynamic_filters ?? currentFilters.value
        currentPage.value = params.page_num
      }

      return response.data
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load similar items'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const clearSimilarItems = () => {
    sourceImageUrl.value = null
    similarItems.value = []
    currentPage.value = 1
    pageInfo.value = null
    totalCount.value = 0
    currentFilters.value = []
    currentSort.value = 'default'
    error.value = null
  }

  // Computed
  const isEmpty = computed(() => similarItems.value.length === 0)

  return {
    // State
    sourceImageUrl,
    similarItems,
    currentPage,
    pageInfo,
    totalCount,
    currentFilters,
    currentSort,
    isLoading,
    error,

    // Actions
    setSourceImage,
    getSimilarItems,
    clearSimilarItems,

    // Computed
    isEmpty
  }
})
